import { ref, useApm, useCoreLogger, type ApiError, type OpenapiError } from '#imports'
import type { ApiResponse, UseMutationMutateOptions } from '../../types'

export function useMutation<TResponse = ApiResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null, TBody = any>(handler: (body: TBody) => Promise<TResponse>, options?: UseMutationMutateOptions<TResponse, TError, TBody>) {
    const apm = useApm()
    const logger = useCoreLogger()

    // Data
    const body = ref<TBody | undefined>()
    const data = ref<TResponse | undefined>()
    const error = ref<TError | undefined>()

    // States
    const pending = ref<boolean>(false)
    const isError = ref<boolean>(false)
    const isSuccess = ref<boolean>(false)

    function reset() {
        body.value = undefined
        data.value = undefined
        error.value = undefined
        isError.value = false
        isSuccess.value = false
    }

    function _shouldRun(mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        const mode = mutateOptions?.mode || options?.mode || 'parallel'

        if (mode === 'leading') return !pending.value

        // Default: parallel
        return true
    }

    async function mutate(_body: TBody, mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        if (!_shouldRun(mutateOptions)) return

        reset()
        pending.value = true

        body.value = _body

        try {
            if (options?.onMutate) {
                await options.onMutate(_body)
            }

            if (mutateOptions?.onMutate) {
                await mutateOptions.onMutate(_body)
            }

            const response = await handler(_body) as TResponse
            data.value = response as typeof data.value
            isSuccess.value = true

            if (options?.onSuccess) {
                await options.onSuccess(response, _body)
            }

            if (mutateOptions?.onSuccess) {
                await mutateOptions.onSuccess(response, _body)
            }
        }
        catch (_error) {
            error.value = _error as typeof error.value
            isError.value = true

            logger.error(_error)
            apm.captureError(_error)

            if (options?.onError) {
                await options.onError(error.value as TError, _body)
            }

            if (mutateOptions?.onError) {
                await mutateOptions.onError(error.value as TError, _body)
            }
        }
        finally {
            if (options?.onSettled) {
                await options.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            if (mutateOptions?.onSettled) {
                await mutateOptions.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            pending.value = false
        }
    }

    async function mutateAsync(_body: TBody, mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        if (!_shouldRun(mutateOptions)) return

        reset()
        pending.value = true

        body.value = _body

        let response: TResponse | undefined = undefined
        let responseError: unknown = undefined

        try {
            if (options?.onMutate) {
                await options.onMutate(_body)
            }

            if (mutateOptions?.onMutate) {
                await mutateOptions.onMutate(_body)
            }

            response = await handler(_body) as TResponse
            data.value = response as typeof data.value
            isSuccess.value = true

            if (options?.onSuccess) {
                await options.onSuccess(response, _body)
            }

            if (mutateOptions?.onSuccess) {
                await mutateOptions.onSuccess(response, _body)
            }
        }
        catch (_error) {
            error.value = _error as typeof error.value
            isError.value = true

            logger.error(_error)
            apm.captureError(_error)

            if (options?.onError) {
                await options.onError(error.value as TError, _body)
            }

            if (mutateOptions?.onError) {
                await mutateOptions.onError(error.value as TError, _body)
            }

            responseError = _error
        }
        finally {
            if (options?.onSettled) {
                await options.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            if (mutateOptions?.onSettled) {
                await mutateOptions.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            pending.value = false
        }

        if (responseError) throw responseError

        return response
    }

    return {
        body,
        data,
        isSuccess,
        error,
        isError,
        pending,
        mutate,
        mutateAsync,
        reset,
    }
}
